import * as R from 'ramda';

import getFilters from '../../selectors/getSelectedFilters';
import getUseRightsholderAttribution from '../../utils/getUseRightsholderAttribution';
import { isFailedToFetchError } from '../../utils/requestErrorHandlers';
import fetchMediaValueReportCached, {
    isMediaValueReportRequestError,
} from './utils/fetchMediaValueReportCached';
import hasFiltersChanged from './utils/hasFiltersChanged';

const getFirstValue = R.path(['values', 0]);
const getTotalImpressions = R.path(['total', 'impressions']);
const getTotalSpMVP = R.path(['total', 'sponsorMediaValue']);
const getTotalFullMVP = R.path(['total', 'fullMediaValue']);
const getTotalVideoViews = R.path(['total', 'videoViews']);

const getCardsTotal = store => async state => {
    const userClientId = state.user.selectedUserClientId;
    const clientId = state.user.selectedClientId;
    const filters = state.clients[userClientId].filters;
    const client = state.user.clientsById[clientId];
    const useRightsholderAttribution = getUseRightsholderAttribution(
        client,
        clientId,
    );
    // Sets Cards as loading
    const loadingState = R.pipe(
        R.assocPath(
            ['clients', userClientId, 'home', 'cards', 'isLoading'],
            true,
        ),
        R.assocPath(
            ['clients', userClientId, 'home', 'cards', 'hasError'],
            false,
        ),
    )(store.getState());

    store.setState(loadingState);

    const changesId = filters.changesId;
    const globFilters = getFilters(filters);

    const topPlatformPayload = {
        ...globFilters,
        groupBy: ['socialPlatform'],
        orderBy: ['sponsorMediaValue-desc'],
        displayColumns: [
            'sponsorMediaValue',
            'impressions',
            'fullMediaValue',
            'videoViews',
        ],
        includeTextTypeExposures: true,
        ...(useRightsholderAttribution
            ? { useRightsholderAttribution: true }
            : {}),
    };

    const topPlacementsPayload = {
        ...globFilters,
        groupBy: ['rightsholderPlacement'],
        orderBy: ['sponsorMediaValue-desc'],
        displayColumns: ['sponsorMediaValue', 'impressions'],
        includeTextTypeExposures: true,
        limit: 1,
        ...(useRightsholderAttribution
            ? { useRightsholderAttribution: true }
            : {}),
    };

    let topPlatforms;
    let topPlacements;

    try {
        [topPlatforms, topPlacements] = await Promise.all([
            fetchMediaValueReportCached(
                topPlatformPayload,
                '?platformfetcher=true',
            ),
            fetchMediaValueReportCached(topPlacementsPayload),
        ]);
    } catch (error) {
        if (
            !isFailedToFetchError(error) &&
            !isMediaValueReportRequestError(error)
        ) {
            throw error;
        }

        if (hasFiltersChanged(store, userClientId, changesId)) return;

        store.setState(
            R.pipe(
                R.assocPath(
                    ['clients', userClientId, 'home', 'cards', 'isLoading'],
                    false,
                ),
                R.assocPath(
                    ['clients', userClientId, 'home', 'cards', 'hasError'],
                    true,
                ),
            )(store.getState()),
            true,
        );
        return;
    }

    const topPlatform = getFirstValue(topPlatforms);
    const topPlacement = getFirstValue(topPlacements);
    const totalImpressions = getTotalImpressions(topPlatforms);
    const totalSpMediaValue = getTotalSpMVP(topPlatforms);
    const totalFullMediaValue = getTotalFullMVP(topPlatforms);
    const totalVideoViews = getTotalVideoViews(topPlatforms);

    if (hasFiltersChanged(store, userClientId, changesId)) return;

    store.setState(
        R.evolve(
            {
                clients: {
                    [userClientId]: {
                        home: {
                            cards: {
                                isLoading: R.F,
                                hasError: R.F,
                                changesId: R.always(changesId),
                                topPlatform: R.always({
                                    name: topPlatform
                                        ? topPlatform.socialPlatformName
                                        : '',
                                    value: topPlatform
                                        ? topPlatform.sponsorMediaValue
                                        : 0,
                                }),
                                topPlacement: R.always({
                                    name: topPlacement
                                        ? topPlacement.rightsholderPlacementName
                                        : '',
                                    value: topPlacement
                                        ? topPlacement.sponsorMediaValue
                                        : 0,
                                }),
                                totalImpressions: R.always(totalImpressions),
                                totalSpMediaValue: R.always(totalSpMediaValue),
                                totalFullMediaValue:
                                    R.always(totalFullMediaValue),
                                totalVideoViews: R.always(totalVideoViews),
                            },
                        },
                    },
                },
            },
            store.getState(),
        ),
        true,
    );
};

export default getCardsTotal;
